import React from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  MDBBtn,
  MDBIcon,
  MDBInput,
  MDBModal,
  MDBModalBody,
  MDBModalHeader,
} from "mdbreact";
import { useToasts } from "react-toast-notifications";

import Stepper from "../../../../../components/stepper";
import { BROWSE as BROWSE_INVENTORY } from "../../../../../services/redux/slices/diagnostics/clinic/clinicInventory";
import { FILTERBYCATEGORY as BROWSE_SUPPLIERS } from "../../../../../services/redux/slices/assets/providers";
import {
  AddItem,
  RemoveItem,
  SAVE,
  SetFormValue,
  SetStep,
  toggleModal,
} from "../../../../../services/redux/slices/diagnostics/clinic/clinicSupplies";
import { getClinicIds } from "./utils";

const steps = [
  { icon: "clipboard-list", label: "Supply Details" },
  { icon: "capsules", label: "Medicines" },
];

const getMedicineDosage = (menu = {}) => {
  if (menu?.dosage) return menu.dosage;
  if (String(menu?.itemType || "").toLowerCase() !== "meds") return "";
  if (!menu?.strength) return menu?.unit || "";

  return `${menu.strength} ${menu.measure || ""}/${menu.unit || "pcs"}`
    .replace(/\s+/g, " ")
    .trim();
};

const formatMedicineLabel = (menu = {}) => {
  const abbreviation = menu?.abbreviation || "Unnamed medicine";
  const dosage = getMedicineDosage(menu);

  return dosage ? `${abbreviation} (${dosage})` : abbreviation;
};

export default function Modal() {
  const dispatch = useDispatch();
  const { addToast } = useToasts();
  const { token, activePlatform, auth } = useSelector(({ auth }) => auth);
  const { collections: clinics = [] } = useSelector(
    ({ clinicInfo }) => clinicInfo,
  );
  const { collections: suppliers = [] } = useSelector(
    ({ providers }) => providers,
  );
  const { collections: menus = [] } = useSelector(
    ({ clinicMenus }) => clinicMenus,
  );
  const { showModal, activeStep, form, formSubmitted } = useSelector(
    ({ clinicSupplies }) => clinicSupplies,
  );
  const [draftItem, setDraftItem] = React.useState({
    menu: "",
    expiry: "",
    qnty: 1,
    unit: "pcs",
  });
  const [draftQuery, setDraftQuery] = React.useState("");

  const clinicOptions = React.useMemo(() => {
    const availableClinicIds = getClinicIds({
      activePlatform,
      clinics,
      authId: auth?._id,
    });

    return clinics.filter((clinic) =>
      availableClinicIds.includes(String(clinic?._id || "")),
    );
  }, [activePlatform, clinics, auth?._id]);

  const medicineOptions = React.useMemo(
    () =>
      menus.filter(
        (menu) =>
          String(menu?.itemType || "").toLowerCase() === "meds" &&
          String(menu?.clinic?._id || menu?.clinic || "") ===
            String(form?.clinic || ""),
      ),
    [menus, form?.clinic],
  );

  const selectedMedicineIds = React.useMemo(
    () =>
      new Set(
        (form?.items || [])
          .map((item) => String(item?.menu || ""))
          .filter(Boolean),
      ),
    [form?.items],
  );

  const availableMedicineOptions = React.useMemo(
    () =>
      medicineOptions.filter((menu) => {
        const menuId = String(menu?._id || "");
        if (!menuId) return false;

        return (
          !selectedMedicineIds.has(menuId) ||
          menuId === String(draftItem?.menu || "")
        );
      }),
    [medicineOptions, selectedMedicineIds, draftItem?.menu],
  );

  const searchMedicineOptions = React.useCallback(
    (query = "") => {
      const normalizedQuery = String(query).trim().toLowerCase();

      if (!normalizedQuery) {
        return availableMedicineOptions.slice(0, 8);
      }

      return availableMedicineOptions
        .filter((menu) => {
          const label = formatMedicineLabel(menu).toLowerCase();
          const description = String(menu?.description || "").toLowerCase();
          return (
            label.includes(normalizedQuery) ||
            description.includes(normalizedQuery)
          );
        })
        .slice(0, 8);
    },
    [availableMedicineOptions],
  );

  const supplierOptions = React.useMemo(() => {
    const optionMap = new Map();

    suppliers
      .filter(
        (supplier) =>
          String(supplier?.category || "").toLowerCase() === "supplier",
      )
      .forEach((supplier) => {
        const value = String(
          supplier?.companyId ||
            supplier?.vendors?.companyId ||
            supplier?._id ||
            "",
        );

        if (!value || optionMap.has(value)) return;

        optionMap.set(value, {
          value,
          label:
            supplier?.company ||
            supplier?.name ||
            supplier?.displayname ||
            supplier?.branch ||
            "Unnamed supplier",
        });
      });

    return Array.from(optionMap.values());
  }, [suppliers]);

  React.useEffect(() => {
    if (!showModal) return;

    if (!form?.clinic && clinicOptions.length) {
      dispatch(
        SetFormValue({
          key: "clinic",
          value: String(clinicOptions[0]?._id || ""),
        }),
      );
    }

    if (token && activePlatform?.branchId) {
      dispatch(
        BROWSE_SUPPLIERS({
          token,
          keys: {
            clients: activePlatform.branchId,
            category: "supplier",
          },
        }),
      );
    }
  }, [
    dispatch,
    showModal,
    clinicOptions,
    form?.clinic,
    token,
    activePlatform?.branchId,
  ]);

  React.useEffect(() => {
    if (showModal) return;

    setDraftItem({
      menu: "",
      expiry: "",
      qnty: 1,
      unit: "pcs",
    });
    setDraftQuery("");
  }, [showModal]);

  const handleClose = () => dispatch(toggleModal());

  const handleNext = () => {
    if (!form?.clinic) {
      return addToast("Please select a clinic first.", { appearance: "error" });
    }

    dispatch(SetStep(1));
  };

  const handleBack = () => dispatch(SetStep(0));

  const handleIncludeItem = () => {
    if (!draftItem?.menu) {
      return addToast("Please select a medicine first.", {
        appearance: "error",
      });
    }

    if (!Number(draftItem?.qnty || 0)) {
      return addToast("Quantity must be at least 1.", {
        appearance: "error",
      });
    }

    dispatch(
      AddItem({
        ...draftItem,
        qnty: Number(draftItem.qnty || 0),
      }),
    );

    setDraftItem({
      menu: "",
      expiry: "",
      qnty: 1,
      unit: "pcs",
    });
    setDraftQuery("");
  };

  const handleSubmit = async () => {
    if (!form?.clinic) {
      return addToast("Clinic is required.", { appearance: "error" });
    }

    if (!form?.items?.length) {
      return addToast("Please add at least one medicine.", {
        appearance: "error",
      });
    }

    if (form.items.some((item) => !item?.menu || !Number(item?.qnty || 0))) {
      return addToast("Complete all medicine rows before saving.", {
        appearance: "error",
      });
    }

    const sanitizedItems = form.items.map((item) => {
      const sanitizedItem = {
        menu: String(item?.menu || "").trim(),
        qnty: Number(item?.qnty || 0),
        unit: String(item?.unit || "pcs").trim() || "pcs",
      };

      const expiry = String(item?.expiry || "").trim();
      if (expiry) sanitizedItem.expiry = expiry;

      const remarks = String(item?.remarks || "").trim();
      if (remarks) sanitizedItem.remarks = remarks;

      return sanitizedItem;
    });

    const sanitizedForm = {
      clinic: String(form?.clinic || "").trim(),
      supplier: String(form?.supplier || "").trim() || null,
      user: auth?._id,
      status: String(form?.status || "draft").trim() || "draft",
      items: sanitizedItems,
    };

    const remarks = String(form?.remarks || "").trim();
    if (remarks) sanitizedForm.remarks = remarks;

    try {
      await dispatch(
        SAVE({
          token,
          data: sanitizedForm,
        }),
      ).unwrap();

      const clinicIds = getClinicIds({
        activePlatform,
        clinics,
        authId: auth?._id,
      });

      if (clinicIds.length) {
        await dispatch(
          BROWSE_INVENTORY({
            token,
            key: { clinicIds: clinicIds.join(",") },
          }),
        );
      }

      addToast("Clinic supply saved successfully.", { appearance: "success" });
    } catch (error) {
      addToast(error.message || "Failed to save clinic supply.", {
        appearance: "error",
      });
    }
  };

  return (
    <MDBModal isOpen={showModal} toggle={handleClose} backdrop size="lg">
      <MDBModalHeader
        toggle={handleClose}
        className="light-blue darken-3 white-text"
      >
        <MDBIcon icon="boxes" className="mr-2" />
        Add Clinic Supply
      </MDBModalHeader>
      <MDBModalBody className="mb-0">
        <Stepper steps={steps} activeStep={activeStep} />

        {activeStep === 0 && (
          <div className="mt-4">
            <small>Clinic</small>
            <select
              className="form-control mb-3"
              value={form?.clinic || ""}
              onChange={(event) =>
                dispatch(
                  SetFormValue({
                    key: "clinic",
                    value: event.target.value,
                  }),
                )
              }
            >
              <option value="">-- Select Clinic --</option>
              {clinicOptions.map((clinic) => (
                <option key={clinic._id} value={clinic._id}>
                  {clinic?.title || clinic?.code || "Unnamed clinic"}
                </option>
              ))}
            </select>

            <small>Supplier</small>
            <select
              className="form-control mb-3"
              value={form?.supplier || ""}
              onChange={(event) =>
                dispatch(
                  SetFormValue({
                    key: "supplier",
                    value: event.target.value,
                  }),
                )
              }
            >
              <option value="">-- Optional Supplier --</option>
              {supplierOptions.map((supplier) => (
                <option key={supplier.value} value={supplier.value}>
                  {supplier.label}
                </option>
              ))}
            </select>

            <small>Status</small>
            <select
              className="form-control mb-3"
              value={form?.status || "draft"}
              onChange={(event) =>
                dispatch(
                  SetFormValue({
                    key: "status",
                    value: event.target.value,
                  }),
                )
              }
            >
              <option value="draft">Draft</option>
              <option value="submit">Submit</option>
              <option value="approve">Approve</option>
              <option value="cancelled">Cancelled</option>
            </select>

            <MDBInput
              label="Remarks"
              type="textarea"
              rows="3"
              value={form?.remarks || ""}
              onChange={(event) =>
                dispatch(
                  SetFormValue({
                    key: "remarks",
                    value: event.target.value,
                  }),
                )
              }
            />
          </div>
        )}

        {activeStep === 1 && (
          <div className="mt-4">
            <div className="border rounded p-3 mb-3">
              <div className="row align-items-end">
                <div className="col-md-6 mb-3">
                  <small>Medicine from Menus</small>
                  <div className="position-relative">
                    <input
                      className="form-control"
                      placeholder="Type medicine name or dosage..."
                      value={
                        draftQuery ||
                        (draftItem?.menu
                          ? formatMedicineLabel(
                              medicineOptions.find(
                                (menu) =>
                                  String(menu?._id || "") ===
                                  String(draftItem?.menu || ""),
                              ),
                            )
                          : "")
                      }
                      onChange={(event) => {
                        const query = event.target.value;
                        setDraftQuery(query);
                        setDraftItem((prev) => ({
                          ...prev,
                          menu: "",
                        }));
                      }}
                    />
                    {draftQuery.trim() !==
                      (draftItem?.menu
                        ? formatMedicineLabel(
                            medicineOptions.find(
                              (menu) =>
                                String(menu?._id || "") ===
                                String(draftItem?.menu || ""),
                            ),
                          )
                        : "") && (
                      <div
                        className="border rounded bg-white shadow-sm mt-1"
                        style={{
                          maxHeight: "220px",
                          overflowY: "auto",
                        }}
                      >
                        {searchMedicineOptions(draftQuery).length ? (
                          searchMedicineOptions(draftQuery).map((menu) => (
                            <button
                              key={menu._id}
                              type="button"
                              className="btn btn-link text-left text-dark w-100 m-0 px-3 py-2 border-bottom"
                              style={{ textDecoration: "none" }}
                              onClick={() => {
                                setDraftItem((prev) => ({
                                  ...prev,
                                  menu: menu._id,
                                  unit: menu?.unit || prev.unit || "pcs",
                                }));
                                setDraftQuery(formatMedicineLabel(menu));
                              }}
                            >
                              <div className="font-weight-bold">
                                {menu?.abbreviation || "Unnamed medicine"}
                              </div>
                              <div className="small text-info">
                                {getMedicineDosage(menu) || "No dosage"}
                              </div>
                              <div className="small text-muted">
                                {menu?.description || "No description"}
                              </div>
                            </button>
                          ))
                        ) : (
                          <div className="px-3 py-2 small text-muted">
                            No matching medicine found.
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                <div className="col-md-2 mb-3">
                  <small>Quantity</small>
                  <input
                    className="form-control"
                    type="number"
                    min="1"
                    value={draftItem?.qnty || 1}
                    onChange={(event) => {
                      const value = event.target.value;
                      setDraftItem((prev) => ({
                        ...prev,
                        qnty: value,
                      }));
                    }}
                  />
                </div>

                <div className="col-md-3 mb-3">
                  <small>Expiration</small>
                  <input
                    className="form-control"
                    type="date"
                    value={draftItem?.expiry || ""}
                    onChange={(event) => {
                      const value = event.target.value;
                      setDraftItem((prev) => ({
                        ...prev,
                        expiry: value,
                      }));
                    }}
                  />
                </div>

                <div className="col-md-1 mb-3">
                  <MDBBtn
                    size="sm"
                    color="info"
                    className="m-0 w-100 px-2"
                    onClick={handleIncludeItem}
                  >
                    +
                  </MDBBtn>
                </div>
              </div>

              {draftItem?.menu && (
                <div className="small text-muted">
                  {(() => {
                    const selectedDraftMenu = medicineOptions.find(
                      (menu) =>
                        String(menu?._id || "") ===
                        String(draftItem?.menu || ""),
                    );

                    return `${selectedDraftMenu?.description || "No medicine description."}${
                      getMedicineDosage(selectedDraftMenu)
                        ? ` (${getMedicineDosage(selectedDraftMenu)})`
                        : ""
                    }`;
                  })()}
                </div>
              )}
            </div>

            <div className="border rounded">
              <div className="table-responsive">
                <table className="table table-sm mb-0">
                  <thead>
                    <tr>
                      <th>Medicine</th>
                      <th>Dosage</th>
                      <th className="text-center">Quantity</th>
                      <th>Expiration</th>
                      <th className="text-center">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {form.items.length ? (
                      form.items.map((item, index) => {
                        const selectedMenu = medicineOptions.find(
                          (menu) =>
                            String(menu?._id || "") ===
                            String(item?.menu || ""),
                        );

                        return (
                          <tr key={`cart-item-${index}`}>
                            <td>
                              {selectedMenu?.abbreviation || "Unnamed medicine"}
                            </td>
                            <td>{getMedicineDosage(selectedMenu) || "-"}</td>
                            <td className="text-center">{item?.qnty || 0}</td>
                            <td>{item?.expiry || "-"}</td>
                            <td className="text-center">
                              <MDBBtn
                                size="sm"
                                color="danger"
                                className="m-0 px-2"
                                onClick={() => dispatch(RemoveItem(index))}
                              >
                                Remove
                              </MDBBtn>
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan="5" className="text-center text-muted py-4">
                          No medicines included yet.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        <div className="d-flex justify-content-between align-items-center mt-4">
          <MDBBtn
            color="secondary"
            onClick={activeStep === 0 ? handleClose : handleBack}
          >
            {activeStep === 0 ? "Cancel" : "Back"}
          </MDBBtn>

          {activeStep === 0 ? (
            <MDBBtn color="info" onClick={handleNext}>
              Next
            </MDBBtn>
          ) : (
            <MDBBtn
              color="success"
              disabled={formSubmitted}
              onClick={handleSubmit}
            >
              Save Supply
            </MDBBtn>
          )}
        </div>
      </MDBModalBody>
    </MDBModal>
  );
}
